<!--
  蛋叔渠道 V2 商品详情页（仅展示）。
  - 进入路径：/DetailsDsV2/:thirdProductId（GoodsListCom / ShopCards 识别 DS 前缀后跳转）
  - 详情接口：GET /dsMallV2/queryProductDetail（带 cardId，后端按福利卡 cardPointType 换算价格 / 单位）
  - 标签：直接读取 detail.labelList（与商品列表 GoodsListCom 同源）
  - 配送范围：detail.deliveryScopeDesc（详情接口同步返回，有则展示）
  - 加购 / 立即购：弹出 SpecPopupDsV2 选规格 / 配送方式 / 门店 / 数量 / 祝福语 / 备注，后续走主体系下单
-->
<template>
  <div class="ds-v2-detail">
    <!-- 轮播主图 -->
    <div class="banner-section">
      <van-swipe class="banner-swipe" :autoplay="6000" indicator-color="white" v-if="bannerList.length > 0">
        <van-swipe-item v-for="(img, idx) in bannerList" :key="idx">
          <van-image :src="img" fit="cover" class="banner-image" />
        </van-swipe-item>
      </van-swipe>
      <div v-else class="banner-placeholder">商品主图加载中…</div>
    </div>

    <!-- 商品信息 -->
    <div class="info-section" v-if="detail">
      <!-- 价格 + 标题标签 -->
      <div class="price-section">
        <div class="price" v-if="detail.isShowPrice !== false">
          <span class="price-value">{{ headerPrice }}</span>
          <span class="price-unit">{{ detail.cardPointTypeUnit || '点' }}</span>
        </div>
        <div class="title-tag" v-if="titleTag" :style="{ background: titleTag.colorCode }">
          {{ getLabelName(titleTag) }}
        </div>
      </div>

      <!-- 商品名称 -->
      <div class="product-name">
        <span class="name">{{ detail.productName }}</span>
      </div>

      <!-- 普通标签 -->
      <div class="tag-list" v-if="otherTags.length">
        <span class="tag-item" v-for="(t, idx) in otherTags" :key="idx">{{ getLabelName(t) }}</span>
      </div>

      <!-- 推荐理由 -->
      <div class="introduce" v-if="detail.introduce">{{ detail.introduce }}</div>
    </div>

    <!-- 规格选择行（点击进入弹窗，与老 Details.vue 保持一致） -->
    <div class="spec-section" v-if="detail">
      <div v-if="detail.brandName" class="spec-item" @click="handleProductList">
        <div class="spec-label">
          <div class="header-line"></div>
          <span class="label-text">品牌</span>
        </div>
        <div class="spec-content">
          <span class="spec-text">{{ detail.brandName }}</span>
          <van-icon name="arrow" color="#c8c8c8" size="14px" />
        </div>
      </div>
      <div class="spec-item" @click="openSpecPopup()">
        <div class="spec-label">
          <div class="header-line"></div>
          <span class="label-text">选择</span>
        </div>
        <div class="spec-content">
          <span class="spec-text">{{ specSummary }}</span>
          <van-icon name="arrow" color="#c8c8c8" size="14px" />
        </div>
      </div>
    </div>

    <!-- 配送范围（详情接口同步返回；有则展示，无则不展示整块） -->
    <div class="scope-block" v-if="detail && detail.deliveryScopeDesc">
      <div class="scope-title">
        <div class="header-line"></div>
        <span>配送范围</span>
      </div>
      <div class="scope-body">{{ detail.deliveryScopeDesc }}</div>
    </div>

    <!-- 商品详情（描述 + 详情图） -->
    <div class="detail-section" v-if="detail">
      <div class="section-header">
        <div class="header-line"></div>
        <span class="header-text">商品详情</span>
      </div>
      <div class="description" v-if="detail.description" v-html="sanitizedDescription"></div>
      <div class="image-list" v-if="detailImageList.length">
        <van-image v-for="(img, idx) in detailImageList" :key="idx" :src="img" class="detail-image" />
      </div>
    </div>

    <!-- 底部操作栏 -->
    <van-goods-action>
      <van-goods-action-icon icon="cart-o" text="购物车" @click="goCar" />
      <van-goods-action-button type="warning" :disabled="!detail" :text="textCar" @click="openSpecPopup('addCart')" />
      <van-goods-action-button type="danger" :disabled="!detail" :text="textBuy" @click="openSpecPopup('buyNow')" />
    </van-goods-action>

    <SpecPopupDsV2 v-if="detail" ref="specPopupRef" :detail="detail" :query-form="queryForm"
      @submit="handleSpecSubmit" @specChange="handleSpecChange"></SpecPopupDsV2>
  </div>
</template>

<script>
import SpecPopupDsV2 from "../../components/SpecPopup/SpecPopupDsV2.vue";
import { sanitizeHtml } from "@/utils/sanitize";
import {
  loadV2Detail,
  goMyCart,
  formatParamsDsV2,
  submitBuyGoodsInfoDsV2,
} from "./detailsDsV2.action";
import { goBrandProductList } from "./index.action";

export default {
  name: "DetailsDsV2",
  components: { SpecPopupDsV2 },
  data() {
    return {
      detail: null,
      // 用户选择的规格（详情页只展示规格摘要 + 价格摘要；详细选择在弹窗里完成）
      selectedSpec: null,
      payCount: 1,
      // 入参（cardId / cityCode / userId）
      queryForm: {
        cardId: "",
        cityCode: "",
        userId: "",
      },
      textCar: "加入购物车",
      textBuy: "立即购买",
    };
  },
  computed: {
    bannerList() {
      if (!this.detail) return [];
      const list = (this.detail.carouselImageList || []).filter(Boolean);
      // 与老 Details.vue 一致：把当前选中的规格图作为首图（如有）
      if (this.selectedSpec && this.selectedSpec.specImg) {
        return [this.selectedSpec.specImg, ...list];
      }
      if (this.detail.imagePath) {
        return [this.detail.imagePath, ...list];
      }
      return list;
    },
    detailImageList() {
      if (!this.detail) return [];
      return (this.detail.detailImageList || []).filter(Boolean);
    },
    sanitizedDescription() {
      return sanitizeHtml(this.detail && this.detail.description ? this.detail.description : "");
    },
    /**
     * 标签：detail.labelList 来自后端 categoryLabelService，与列表同源；smallLabelType=1 为标题标签，其他为普通标签。
     */
    titleTag() {
      const list = this.detail && this.detail.labelList ? this.detail.labelList : [];
      return list.find(it => String(it.smallLabelType) === "1");
    },
    otherTags() {
      const list = this.detail && this.detail.labelList ? this.detail.labelList : [];
      return list.filter(it => String(it.smallLabelType) !== "1");
    },
    /**
     * 头部价格：未选规格时取 specList[0]，已选则用 selectedSpec.showPrice
     */
    headerPrice() {
      if (this.selectedSpec && this.selectedSpec.showPrice != null) return this.selectedSpec.showPrice;
      const list = this.detail && this.detail.specList ? this.detail.specList : [];
      return list.length && list[0].showPrice != null ? list[0].showPrice : "--";
    },
    specSummary() {
      if (!this.selectedSpec) return "请选择规格";
      return this.selectedSpec.specifications || this.selectedSpec.specName || "请选择规格";
    },
  },
  mounted() {
    this.scrollPageToTop();
    // 微信 Android 进入详情时可能仍保留列表页滚动位置，延迟再复位一次
    const scrollTimer = setInterval(() => {
      this.scrollPageToTop();
      clearInterval(scrollTimer);
    }, 200);
    this.init();
  },
  methods: {
    /** 进入详情页置顶（与 Details.vue / DetailsDsCardCoupons 一致，兼容微信内置浏览器） */
    scrollPageToTop() {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    },
    /**
     * CategoryLabelDTO 没有名称字段；列表里也是按 smallLabelType / colorCode 显示。
     * 这里兼容多种命名（name / labelName / smallLabelName）。
     */
    getLabelName(label) {
      if (!label) return "";
      return label.name || label.labelName || label.smallLabelName || "";
    },
    init() {
      const userInfo = localStorage.getItem("userInfo");
      this.queryForm.userId = userInfo ? JSON.parse(userInfo).id : "";
      this.queryForm.cardId = localStorage.getItem("cardId") || "";
      this.queryForm.cityCode = localStorage.getItem("cityCode") || "";
      this.loadDetail();
    },
    async loadDetail() {
      const thirdProductId = this.$route.params.thirdProductId || this.$route.query.thirdProductId;
      const detail = await loadV2Detail(this.queryForm.cityCode, thirdProductId, this.queryForm.cardId);
      if (!detail) {
        const timer = setTimeout(() => {
          this.$router.back();
          clearTimeout(timer);
        }, 800);
        return;
      }
      this.detail = detail;
      // 详情数据与轮播图渲染后再次置顶，避免异步布局导致首屏偏移（安卓微信常见）
      this.$nextTick(() => {
        this.scrollPageToTop();
        requestAnimationFrame(() => this.scrollPageToTop());
      });
      const list = detail.specList || [];
      if (list.length > 0) {
        // 适配老组件用 id 字段
        this.selectedSpec = {
          ...list[0],
          id: list[0].thirdSpecId,
          specifications: list[0].specName,
          coverImage: list[0].specImg || detail.imagePath,
          isShowPrice: list[0].isShowPrice == null ? detail.isShowPrice : list[0].isShowPrice,
          cardPointTypeUnit: list[0].cardPointTypeUnit || detail.cardPointTypeUnit,
        };
      }
    },
    openSpecPopup(intent) {
      const buttonTexts = { textBuy: this.textBuy, textCar: this.textCar };
      // intent 为 undefined 时（点"选择"行）打开 both
      this.$refs.specPopupRef.open(intent, buttonTexts);
    },
    goCar() {
      goMyCart(2);
    },
    handleProductList() {
      if (this.detail && this.detail.brandName) {
        goBrandProductList(this.detail.brandName);
      }
    },
    handleSpecChange({ selectedSpec }) {
      this.selectedSpec = selectedSpec || this.selectedSpec;
    },
    /**
     * SpecPopupDsV2 提交回调：组装 payload → 走主体系（addShop / prePurchase）。
     */
    async handleSpecSubmit({ type, selectedSpec, deliveryData }) {
      if (!this.detail) return;
      const baseData = {
        userId: this.queryForm.userId,
        cardId: this.queryForm.cardId,
        cityCode: this.queryForm.cityCode,
        productId: this.detail.thirdProductId, // 老接口字段（DS 前缀）
        type: "3", // 蛋糕类
      };
      const v2 = {
        thirdProductId: this.detail.thirdProductId,
        thirdSpecId: selectedSpec && selectedSpec.id ? selectedSpec.id : "",
      };
      const payload = formatParamsDsV2(baseData, selectedSpec, deliveryData, { v2 });
      this.selectedSpec = selectedSpec;
      this.payCount = selectedSpec.count || 1;
      await submitBuyGoodsInfoDsV2(type, payload);
    },
  },
};
</script>

<style scoped lang="scss">
.ds-v2-detail {
  background: #f8f8f8;
  min-height: 100vh;
  padding-bottom: 60px;
  overflow-x: hidden;

  .banner-section {
    width: 100%;
    height: 100vw;
    position: relative;
    background: #fff;
    flex-shrink: 0;

    .banner-swipe,
    .banner-image {
      width: 100%;
      height: 100%;
    }

    .banner-placeholder {
      height: 100%;
      background: #f5f5f5;
      color: #999;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .info-section {
    background: #fff;
    padding: 16px;
    margin-bottom: 8px;

    .price-section {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;

      .price {
        .price-value {
          font-size: 28px;
          font-weight: 600;
          color: #d23b2e;
          font-family: DIN;
        }

        .price-unit {
          font-size: 14px;
          color: #d23b2e;
          margin-left: 2px;
        }
      }

      .title-tag {
        padding: 4px 8px;
        border-radius: 4px;
        color: #fff;
        font-size: 12px;
        font-weight: 500;
      }
    }

    .product-name {
      margin-bottom: 12px;
      line-height: 1.4;

      .name {
        font-size: 16px;
        font-weight: 600;
        color: #333;
      }
    }

    .tag-list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      .tag-item {
        padding: 4px 8px;
        background: #f5f5f5;
        border-radius: 4px;
        font-size: 12px;
        color: #666;
      }
    }

    .introduce {
      margin-top: 8px;
      font-size: 13px;
      color: #666;
    }
  }

  .spec-section {
    background: #fff;
    margin-bottom: 8px;
    border-radius: 8px;

    .spec-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 18px 16px;
      border-bottom: 1px solid #f5f5f5;

      &:last-child {
        border-bottom: none;
      }

      .spec-label {
        display: flex;
        align-items: center;
        min-width: 60px;

        .label-text {
          font-size: 15px;
          color: #333;
          font-weight: 500;
        }
      }

      .spec-content {
        display: flex;
        align-items: center;
        flex: 1;
        justify-content: flex-end;
        gap: 10px;

        .spec-text {
          font-size: 14px;
          color: #666;
          text-align: right;
          max-width: 180px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          line-height: 1.4;
        }
      }
    }
  }

  .scope-block {
    background: #fff;
    margin-bottom: 8px;
    padding: 14px 16px;

    .scope-title {
      display: flex;
      align-items: center;
      font-size: 15px;
      font-weight: 600;
      color: #333;
      margin-bottom: 8px;
    }

    .scope-body {
      font-size: 13px;
      color: #666;
      line-height: 1.6;
      word-break: break-all;
    }
  }

  .detail-section {
    background: #fff;
    padding: 16px;

    .section-header {
      display: flex;
      align-items: center;
      margin-bottom: 16px;

      .header-text {
        font-size: 16px;
        font-weight: 600;
        color: #333;
      }
    }

    .description {
      font-size: 15px;
      color: #333;
      line-height: 1.7;
      margin-bottom: 20px;
      white-space: pre-wrap;
      word-break: break-word;

      :deep(img) {
        max-width: 100%;
        height: auto;
        margin: 10px 0;
        border-radius: 4px;
      }
    }

    .image-list {
      font-size: 0;

      .detail-image {
        width: 100%;
        margin: 0;
        border-radius: 0;
        display: block;
      }
    }
  }
}

:deep(.van-goods-action) {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);

  .van-goods-action-button {
    height: 40px;
    font-size: 15px;
  }
}

.header-line {
  width: 3px;
  height: 16px;
  background: #d23b2e;
  margin-right: 8px;
  border-radius: 2px;
}
</style>
